import { Card, Grid, GridItem, Spinner } from '@chakra-ui/react';
import {
  IConfirmModalProps,
  SettingCardType,
  SettingCardViewAction,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { FC } from 'react';
import { IconType } from 'react-icons/lib';
import { SETTING_CARD_LOGOS } from '../../constants/settings';
import { MButton, MFlex, MFormField, MText } from '../Monetize';
import {
  ActionType,
  DataTableActions,
} from '../Monetize/DataTable/MDataTableActions';
import { MEntityStatusBadge } from '../Monetize/MEntityStatusBadge';

const StatusDisplay = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ERROR: 'Error',
} as const;

interface SettingCardViewProps {
  type: SettingCardType;
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR';
  omitEditButton?: boolean;
  isLoading?: boolean;
  isEnabled?: boolean;
  tags: {
    label: string;
    tooltip?: Maybe<string>;
    value: string;
    colSpan?: null | 1 | 2;
  }[];
  actions: {
    action: SettingCardViewAction;
    label: string;
    icon?: IconType;
    isDanger?: boolean;
    confirmProps?: Partial<IConfirmModalProps> | null;
  }[];
  onEdit: () => void;
  onAction: (action: SettingCardViewAction) => void;
}

export const SettingCardView: FC<SettingCardViewProps> = ({
  type,
  tags,
  actions,
  status,
  omitEditButton,
  isLoading,
  isEnabled = true,
  onEdit,
  onAction,
}) => {
  const Logo = SETTING_CARD_LOGOS[type];

  const tableActions: ActionType[] = actions.map(
    ({ action, isDanger, label, icon, confirmProps }) => ({
      title: label,
      icon,
      action: () => onAction(action),
      color: isDanger ? 'danger' : undefined,
      enabled: true,
      confirmProps,
    }),
  );

  return (
    <Card.Root variant="edit" w="600px">
      <Card.Header>
        <MFlex justifyContent="space-between" alignItems="center">
          <MFlex alignItems="center">
            <Logo />
            <MText ml={2} fontSize="sm">
              {type}
            </MText>
            <MEntityStatusBadge status={StatusDisplay[status]} />
          </MFlex>
          <MFlex alignItems="center">
            {!omitEditButton && !isLoading && (
              <MButton
                variant="tertiary"
                size="sm"
                onClick={onEdit}
                disabled={!isEnabled}
              >
                Edit
              </MButton>
            )}
            <DataTableActions actions={tableActions} />
          </MFlex>
        </MFlex>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <MFlex w="full" justifyContent="center" alignItems="center">
            <Spinner />
          </MFlex>
        )}
        {!isLoading && (
          <Grid gap={4} templateColumns="repeat(2, 275px)" w="100%">
            {tags.map(({ label, value, colSpan, tooltip }) => (
              <GridItem key={label} colSpan={colSpan || 2}>
                <MFormField
                  key={label}
                  label={label}
                  tooltip={tooltip}
                  minW="6rem"
                  width="unset"
                  overflow="hidden"
                >
                  <MText truncate title={value}>
                    {value}
                  </MText>
                </MFormField>
              </GridItem>
            ))}
          </Grid>
        )}
      </Card.Body>
    </Card.Root>
  );
};
